<template>
  <BContentBox v-loading="loading">
    <template #content>
      <div class="content-container">
        <div class="section">
          <div class="title">
            {{ $t('followUpPromptSetting.hearingItems.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.hearingItems.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('followUpPromptSetting.hearingItems.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('followUpPromptSetting.hearingItems.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="hearingItems"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>
        <div class="section">
          <div class="title">
            {{ $t('followUpPromptSetting.hearingItemDescriptions.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.hearingItemDescriptions.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('followUpPromptSetting.hearingItemDescriptions.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('followUpPromptSetting.hearingItemDescriptions.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="hearingItemDescriptions"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>
        <div class="section">
          <div class="title">
            {{ $t('followUpPromptSetting.judgmentCondition.title') }}
          </div>
          <div class="explain">
            {{ $t('followUpPromptSetting.judgmentCondition.description') }}
          </div>
          <div class="example">
            <BExpansionPanel>
              <template #header>
                <div class="example-title">
                  {{ $t('followUpPromptSetting.judgmentCondition.example.title') }}
                </div>
              </template>
              <div class="example-content">
                {{ $t('followUpPromptSetting.judgmentCondition.example.description') }}
              </div>
            </BExpansionPanel>
          </div>
          <div class="mb-300">
            <BTextarea
              v-model="judgmentCondition"
              required
              class="form"
              :autosize="{ minRows: 12, maxRows: 30 }"
            />
          </div>
        </div>

        <BLayout>
          <BBtn
            type="primary"
            :disabled="loading"
            @click="handleSaveClick"
          >
            {{ $t('general.save.text') }}
          </BBtn>
        </BLayout>
      </div>
    </template>
  </BContentBox>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { PromptSettingApiService } from '@/api/user/resources/prompt_setting';
import { useWait } from '@/composable/vue-wait';

const hearingItems = ref<string>();
const hearingItemDescriptions = ref<string>();
const judgmentCondition = ref<string>();

const { doActionWithWait, wait } = useWait();

const loading = computed(() => wait.is(['fetchPromptSettingWait', 'putPromptSettingWait']));

const fetchPromptSetting = () => {
  doActionWithWait('fetchPromptSettingWait', async () => {
    const api = new PromptSettingApiService();
    const { data } = await api.getFollowUpPromptSetting({
      errorHandlers: {
        404: () => {},
      },
    });
    hearingItems.value = data.promptSetting.hearingItems;
    hearingItemDescriptions.value = data.promptSetting.hearingItemDescriptions;
    judgmentCondition.value = data.promptSetting.judgmentCondition;
  });
};

const handleSaveClick = async () => {
  doActionWithWait('putPromptSettingWait', async () => {
    const api = new PromptSettingApiService();
    await api.putFollowUpPromptSetting({
      request: {
        getFollowUpPromptSetting200Response: {
          promptSetting: {
            hearingItems: hearingItems.value,
            hearingItemDescriptions: hearingItemDescriptions.value,
            judgmentCondition: judgmentCondition.value,
          },
        },
      },
    });
  });
};

onBeforeMount(() => {
  fetchPromptSetting();
});
</script>

<style lang="scss" scoped>
.content-container {
  max-width: 900px;
}
.title {
  font-size: $fontsize-400;
  font-weight: bold;
  margin-bottom: $basespace-150;
}
.explain {
  color: $textcolor-light;
  white-space: pre-wrap;
  margin-bottom: $basespace-400;
}
.example {
  margin-bottom: $basespace-200;
}
.example :deep(*){
  color: $textcolor-light;
}
.form {
  width: 100%;
}
.example-content {
  @include m-break-word-pre-wrap
}
</style>
